import React, {
    useCallback, useEffect, useMemo, useState
} from 'react'
import styled from 'styled-components'
import { Link, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { format } from 'date-fns'
import { timeFormatSelector } from 'features/events/eventSlice'
import { _, colors } from 'utils'
import { useDeleteItem, useEventSlug } from 'hooks'
import { CardHeader, CardWrapper, NotFound } from 'components'
import Button from 'components/Button'
import Icon from 'components/Icon'
import DataGrid, { ColumnDataGrid, DataGridProps } from 'components/DataGrid'
import { GroupSessionType } from 'generated/graphql'
import { RequestStatus } from 'types'
import routes from 'routes'
import { Pagination } from '../pagination'
import { deleteGroupSession } from './graphql/mutation'
import { useGroupSessions } from './hooks'
import { addModal } from '../modals/modalSlice'
import CreateGroupSession from './CreateGroupSession'

export const MAX_PARTICIPANTS = 10

const S = {
    TopToolbar: styled.div`
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        border-bottom: 1px solid ${colors.gray1};
    `,
    InfoWrapper: styled.div`
        display: flex;
        align-items: center;
        min-width: 0;
        max-width: 900px;
        overflow: hidden;
        padding-left: 8px;
    `,
    Title: styled.div`
        height: 40px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        overflow: hidden;
        font-weight: 500;
        font-size: 14px;
        line-height: 16px;
        color: ${colors.grayTitle};
    `,
    Date: styled.div`
        display: flex;
        align-items: center;
    `,
    Icon: styled(Icon)`
        margin-top: -2px;
        margin-right: 5px;
    `,
    Action: styled.div`
        text-align: right;
        white-space: nowrap;
        padding-right: 8px;
    `,
}

const createColumns = ({ eventSlug, onDelete, timeFormat }): ColumnDataGrid[] => [
    {
        name: 'info',
        width: 6,
        render: (groupSession: GroupSessionType) => (
            <S.InfoWrapper>
                <S.Title>
                    {groupSession.title}
                </S.Title>
            </S.InfoWrapper>
        )
    },
    {
        name: 'time',
        width: 4,
        render: (groupSession: GroupSessionType) => {
            const startDate = new Date(groupSession.startsAt)
            const endDate = new Date(groupSession.endsAt)
            const hasSameDate = startDate.getFullYear() === endDate.getFullYear()
                && startDate.getMonth() === endDate.getMonth()
                && startDate.getDate() === endDate.getDate()
            return (
                <S.Date>
                    <S.Icon icon="calendar" size={18} />
                    {`${format(startDate, `dd/MM/yyyy | ${timeFormat}`)} - ${format(endDate, hasSameDate ? timeFormat : `dd/MM/yyyy | ${timeFormat}`)}`}
                </S.Date>
            )
        }
    },
    {
        name: 'participants',
        width: 1,
        render: (groupSession: GroupSessionType) => (
            <S.Date>
                <S.Icon icon="group" size={18} />
                {`${groupSession.participants.totalCount}/${MAX_PARTICIPANTS}`}
            </S.Date>
        )
    },
    {
        name: 'actions',
        width: 1,
        render: (groupSession: GroupSessionType) => (
            <S.Action>
                <Link to={routes.updateGroupSession.getPath({ eventSlug, id: groupSession.id })}>
                    <Icon icon="info" size={20} />
                </Link>
                <Icon icon="trash" className="ml-2" size={20} onClick={() => onDelete(groupSession.id)} />
            </S.Action>
        )
    }
]

type GroupSessionDataGridProps = Omit<DataGridProps, 'data'|'columns'> & {
    groupSessions: GroupSessionType[]
    fetchStatus: RequestStatus
    onDelete: (id: number) => void
}
const GroupSessionDataGrid = ({
    groupSessions,
    fetchStatus,
    onDelete,
    ...props
}: GroupSessionDataGridProps) => {
    const eventSlug = useEventSlug()
    const history = useHistory()
    const timeFormat = useSelector(timeFormatSelector)
    const columns = useMemo(
        () => createColumns({ eventSlug, onDelete, timeFormat }), [eventSlug, onDelete, timeFormat]
    )

    if (fetchStatus === 'failed') {
        return <NotFound />
    }

    return (
        <DataGrid
            data={groupSessions}
            columns={columns}
            hideHeader
            hideAction
            {...props}
            onRowClick={(row) => history.push(routes.updateGroupSession.getPath({ eventSlug, id: row.id }))}
            requestStatus={fetchStatus}
        />
    )
}

const GroupSessionList = () => {
    const eventSlug = useEventSlug()
    const dispatch = useDispatch()
    const [groupSessions, setGroupSessions] = useState<GroupSessionType[]>([])

    const {
        list, requestStatus, pagination, totalItems, handlePageChange, fetchData
    } = useGroupSessions({ eventId: eventSlug })

    // remove deleted speaker from the list without calling api to reload
    const removeGroupSession = useCallback(() => {
        fetchData()
    }, [fetchData])
    const { showDeleteConfirmModal } = useDeleteItem(deleteGroupSession, removeGroupSession)

    const showAddModal = useCallback(() => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <CreateGroupSession close={close} onSubmitted={fetchData} />
            ),
            shouldNotWrap: true
        }))
    }, [dispatch, fetchData])

    useEffect(() => {
        setGroupSessions(list)
    }, [list])

    return (
        <CardWrapper>
            <S.TopToolbar>
                <CardHeader>
                    {_('group_sessions.list_header')}
                </CardHeader>
                <Button
                    icon="plusBox"
                    size="sm"
                    onClick={showAddModal}
                >
                    {_('group_sessions.add')}
                </Button>
            </S.TopToolbar>
            <GroupSessionDataGrid
                groupSessions={groupSessions}
                fetchStatus={requestStatus}
                onDelete={showDeleteConfirmModal}
            />
            <Pagination
                page={pagination?.page}
                onPageChange={handlePageChange}
                totalItems={totalItems}
                disabled={requestStatus === 'loading'}
            />
        </CardWrapper>
    )
}

export default GroupSessionList
